import { useEffect, useRef, useState } from "react";
import PageContainer from "@components/layout/PageContainer";
import { useNavigate } from "react-router-dom";
import ModelIndex, { ModelIndexHandle } from "@components/layout/ModelIndex";
import { faCopy, faEnvelope, faEye, faList, faPaperPlane, faPrint, faTruck } from "@fortawesome/free-solid-svg-icons";
import { InvoiceApi, InvoiceDataType } from "@/api/InvoiceApi";
import { formatMoneyValue } from "@utils/Currency";
import { showAlert } from "@utils/Alert";
import styles from "./style.module.scss";
import moment from "moment";
import { EasyposEInvoice } from "@/atv/submitInvoice";
import { useAppSelector } from "@/store/hooks";
import { EDOCUMENT_SUBMITION_STATUS_CODE, EDOCUMENT_SUBMITION_STATUS_SENT } from "@/atv/eInvoiceApi";
import LoadingScreen from "@components/content/LoadingScreen";
import { sendEInvoiceData } from "@utils/EDocument";
import { renderEDocumentStatusOffCanvasTrigger } from "@utils/ModelIndexUtils";
import { OpenSendAtvDocumentPubSub, OpenSendDocumentPubSub } from "@utils/pubsubs/PubSub";
import SendDocumentToEmailOffCanvas from "@components/layout/Offcanvas/SendDocumentToEmailOffCanvas";
import { generateAndUploadPdf } from "@utils/htmlExport";
import { documentPdfS3Key, documentPrintPath } from "@utils/documentPdf";
import SendAtvDocumentToEmailOffCanvas from "@components/layout/Offcanvas/SendAtvDocumentToEmailOffCanvas";
import { ClientApi } from "@/api/ClientApi";
import { INVOICE_HOLDING_TYPE_CODE, TICKET_PRINT_FORMANT_ID } from "@utils/Constants";
import { checkInvoiceTicketPrint } from "@utils/ticketPrintUtils";
import { shippingGuideUrl } from "@utils/ShippingGuide";

type InvoiceIndexProps = {
  onlyHolds?: boolean
}

const InvoiceIndex = (props: InvoiceIndexProps) => {
  const { onlyHolds = false } = props
  const navigate = useNavigate();
  const modelRef = useRef<ModelIndexHandle>(null);
  const suscriptionData = useAppSelector(state => state.suscription.suscription)
  const [easyposEInvoice, setEasyposEInvoice] = useState<EasyposEInvoice>()
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const additionalFilter = onlyHolds ? `tipo = ${INVOICE_HOLDING_TYPE_CODE} ` : ''

  useEffect(() => {
    if (suscriptionData) {
      setEasyposEInvoice(new EasyposEInvoice(suscriptionData))
    }
  }, [suscriptionData]);

  /**
   * Reenvio a Hacienda desde el listado. Regenera el PDF ANTES de mandar el
   * documento: este es el camino al que llega el usuario justamente cuando la
   * generacion del PDF fallo al emitir (el fallo aborta el envio a Hacienda), asi
   * que si no se regenera aqui, el correo sale con el XML y sin PDF para siempre.
   */
  const resendEInvoice = async (invoiceId: string, item: InvoiceDataType) => {
    setIsLoading(true);
    try {
      if (easyposEInvoice) {
        await generateAndUploadPdf(
          documentPrintPath("Invoice", invoiceId),
          documentPdfS3Key("Invoice", suscriptionData?.numero, `${item.invoice_key}`),
        )
        const response = await sendEInvoiceData(easyposEInvoice, invoiceId)
        if (response.status == EDOCUMENT_SUBMITION_STATUS_CODE[EDOCUMENT_SUBMITION_STATUS_SENT]) {
          showAlert("success", "La factura ha sido reenviada correctamente, el estado de la factura se actualizara una vez Tributacion de una respuesta sobre la Factura")
        } else {
          showAlert("danger", "La factura no ha sido reenviada correctamente")
        }
      } else {
        showAlert("danger", "No fue possible enviar el document a hacienda ")
      }
    } finally {
      setIsLoading(false)
    }
  }

  const triggerEmailSend = (item: InvoiceDataType) => {
    OpenSendAtvDocumentPubSub.publish(
      {
        documentId: item.id,
        openOffCanvas: true,
        documentKind: "Invoice",
        suscription_numero: Number(suscriptionData?.numero || 0),
        documentkey: `${item.invoice_key}`,
        clientEmail: `${item.email}`,
        emailBody: `${suscriptionData?.invoice_email}`,
        emailSubject: `${suscriptionData?.nombre} -- Factura #${item.numero}`,
        Suscription: {
          email: `${suscriptionData?.email}`
        }
      })
  }

  const triggerPrintInvoice = (item: InvoiceDataType) => {
    if (suscriptionData?.id && item?.id) {
      if (suscriptionData.formato_impresion == TICKET_PRINT_FORMANT_ID) {
        checkInvoiceTicketPrint(suscriptionData, item)
      } else {
        window.open(`/user/invoices/print/${item.id}`)
      }
    }
  }

  return (
    <PageContainer
      title={`${onlyHolds ? 'Lista de Apartados' : 'Lista Facturas'}`}
      backUrl="/user/dashboard"
      primaryActions={!onlyHolds ? [
        {
          onClick: () => {
            navigate('/user/invoices/add')
          },
          actionTitle: "Nuevo",
          color: "success"
        }
      ] : []
      }
      secondaryActions={!onlyHolds ? [
        {
          actionTitle: "Apartados",
          href: "/user/invoices/holds"
        },
        {
          actionTitle: "Plantillas de Factura",
          href: "/user/invoicetemplates"
        },
        {
          actionTitle: "Cargar XML",
          href: "/user/invoices/import"
        }
      ] : []}
    >
      <ModelIndex<InvoiceDataType>
        ref={modelRef}
        messages={{
          noResults: onlyHolds ? "No hay apartados creados" : "No hay Facturas creadas aun"
        }}
        meiliConfig={{
          index: "invoices",
          sort: ["fecha:desc"],
          filter: additionalFilter
        }}
        fetchAdditionaItems={(searchCriteria: string) => InvoiceApi.searchByInvoiceNumber(searchCriteria)}
        fetchData={(...args) => InvoiceApi.fetchAll(...args)}
        filterProps={[
          {
            key: "sequence",
            filterType: "eq"
          },
          {
            key: "nombre_factura",
            filterType: "contains"
          },
          {
            key: "clientId",
            filterType: "eq",
            placeholder: "Cliente",
            options: async () => ClientApi.optionsList()
          },
          {
            key: "invoice_key",
            filterType: "eq"
          },
          {
            key: "numero",
            filterType: "eq",
            type: "number"
          }
        ]}
        primaryProperties={[
          {
            key: "sequence",
            highlighted: true,
            className: styles.sequence,
            optionalHighlighted: (val: any, data: InvoiceDataType) => { return data.anulada || false },
            highlightedColors: {
              "true": {
                "bg": "#dc3545",
                "text": "#fff"
              },
              "false": {
                "bg": "#337ab7",
                "text": "#fff"
              }
            },
          },
          {
            key: (d) => d.nombre_factura || d.client?.nombre || "",
            alignment: "Start"
          },
        ]}
        primaryPropertiesSizing={
          [
            { sm: "4", xs: "12" },
            { sm: "8", xs: "12" }
          ]
        }
        secondaryProperties={[
          {
            key: (data) => {
              const date = moment(data.fecha, "YYYY-MM-DD")
              return date.format("DD-MM-YYYY")
            },
            title: "Fecha",
          },
          {
            key: (data) => renderEDocumentStatusOffCanvasTrigger(data, { model: "Invoice", id: `${data.id}` }),
            title: "Estado ATV"
          },
          {
            key: (data) => {
              if ("currency_id" in data) {
                return formatMoneyValue(data.total, (data as any).currency_id)
              }
              return formatMoneyValue(data.total, data.currency.currency_id)
            },
            title: "Total",
            highlighted: true
          }
        ]}
        secondaryPropertiesSizing={
          [
            { sm: "4", md: "2" },
            { sm: "4", md: "3" },
            { sm: "4", md: "2" }
          ]
        }
        listActions={[
          {
            isVisible: (data) => !data.edocument_status_payload,
            onClick: (id, item) => resendEInvoice(id, item),
            actionTitle: "Reenviar Factura al ATV",
            iconProps: { icon: faPaperPlane }
          },
          {
            isVisible: () => onlyHolds,
            href: "/user/invoices/payments/{:id}",
            actionTitle: "Ver registro de pago de apartado",
            iconProps: { icon: faList }
          },
          {
            isVisible: () => !onlyHolds,
            href: "/user/invoices/copy/{:id}",
            actionTitle: "Copiar de Facturas",
            iconProps: { icon: faCopy }
          },
          {
            onClick: (id, item) => triggerEmailSend(item),
            actionTitle: "Enviar Por Correo ",
            iconProps: { icon: faEnvelope }
          },
          {
            href: "/user/invoices/view/{:id}",
            actionTitle: "Ver Factura",
            target: "_blank",
            iconProps: { icon: faEye }
          },
          {
            onClick: (id, item) => triggerPrintInvoice(item),
            actionTitle: "Imprimir Factura",
            target: "_blank",
            iconProps: { icon: faPrint }
          },
          {
            // Sin bultos la factura no genero guia, asi que no hay nada que reimprimir.
            isVisible: (data) => Number(data.bultos || 0) > 0,
            onClick: (id) => window.open(shippingGuideUrl(id)),
            actionTitle: "Reimprimir Guia de Envio",
            target: "_blank",
            iconProps: { icon: faTruck }
          }
        ]}
      >
      </ModelIndex>
      <LoadingScreen isLoading={isLoading} />

      <SendAtvDocumentToEmailOffCanvas
        title="Envio de Factura a Email" />

    </PageContainer >
  );
}

export default InvoiceIndex;
